import { forwardRef, useRef, type ComponentPropsWithoutRef, type ForwardedRef } from "react";

type DateInputProps = Omit<ComponentPropsWithoutRef<"input">, "type">;
type PickerCapableInput = HTMLInputElement & { showPicker?: () => void };

function assignForwardedRef<T>(ref: ForwardedRef<T>, value: T | null) {
  if (typeof ref === "function") {
    ref(value);
    return;
  }

  if (ref) {
    ref.current = value;
  }
}

function openDatePicker(input: PickerCapableInput | null) {
  if (!input || input.disabled || input.readOnly) {
    return;
  }

  input.focus();

  if (typeof input.showPicker === "function") {
    try {
      input.showPicker();
    } catch {
      // Browsers without showPicker still keep native keyboard editing.
    }
  }
}

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(function DateInput(
  { className, ...props },
  forwardedRef
) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const setInputRef = (node: HTMLInputElement | null) => {
    inputRef.current = node;
    assignForwardedRef(forwardedRef, node);
  };

  const handleCalendarButtonClick = () => {
    openDatePicker(inputRef.current as PickerCapableInput | null);
  };

  return (
    <span className={`date-input${props.disabled ? " is-disabled" : ""}`}>
      <button
        type="button"
        className="date-input__button"
        aria-label="Open calendar"
        onClick={handleCalendarButtonClick}
        disabled={props.disabled}
      >
        <svg viewBox="0 0 24 24" focusable="false">
          <rect x="3.5" y="5" width="17" height="15.5" rx="3" />
          <path d="M7.5 3.5v4" />
          <path d="M16.5 3.5v4" />
          <path d="M3.5 10.5h17" />
        </svg>
      </button>
      <input
        {...props}
        ref={setInputRef}
        type="date"
        className={["date-input__control", className].filter(Boolean).join(" ")}
      />
    </span>
  );
});
